import React, { useState, useEffect, useCallback } from "react";
import { useProfile } from "./contexts/ProfileContext";
import { supabase } from "./supabaseClient";
import ProfileSelection from "./components/ProfileSelection";
import { useAudioPlayer } from "./hooks/useAudioPlayer";
import { useUISounds } from "./hooks/useUISounds";
import { DialogControls } from "./components/DialogControls";
import {
  prepareSessionForProfile,
  getCurrentOpenSession,
} from "./utils/prepareSessionForProfile";
import { STATE_DONE, STATE_REDO, STATE_READY } from "./constants/session";

const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL ||
  "https://example.com/iseeabunny";

const TEACHER_GENERIC_AUDIO_URL = `${MEDIA_BASE_URL}/gen-audio/teacher_generic.mp3`;

const SessionComplete = ({
  onPrepareSession,
  prepareSessionLoading,
  prepareSessionSuccess,
  onStartNewSession,
}) => {
  const { playButtonClick } = useUISounds();

  return (
    <div className="w-full h-full flex flex-col items-center justify-center p-4 portrait-container">
      <div className="portrait-controls landscape-section-right w-full flex justify-center">
        <div className="landscape-controls-container flex flex-col items-center gap-6 text-center max-w-md mx-auto">
          <div className="text-center w-full">
            <h2 className="text-2xl font-bold mb-2">
              Done for now. Bunny needs a break!
            </h2>
            <p className="text-base text-base-content/70">
              Great job! You've completed all the words in this session.
            </p>
          </div>
          <div className="w-full flex justify-center">
            {prepareSessionSuccess ? (
              <button
                className="btn btn-primary btn-xl gap-2 w-full max-w-xs mx-auto"
                onClick={() => {
                  playButtonClick();
                  onStartNewSession();
                }}
              >
                <span>Start a New Session Now</span>
              </button>
            ) : (
              <button
                className="btn btn-primary btn-xl gap-2 w-full max-w-xs mx-auto"
                onClick={() => {
                  playButtonClick();
                  onPrepareSession();
                }}
                disabled={prepareSessionLoading}
              >
                {prepareSessionLoading ? (
                  <span className="loading loading-spinner"></span>
                ) : (
                  <>
                    <span>See you next time</span>
                    <span>👋</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function DialogApp() {
  const { selectedProfileId, selectedProfile } = useProfile();
  const [dialog, setDialog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(0);
  const [userAnsweredCorrectly, setUserAnsweredCorrectly] = useState(null);
  const [attemptSubmitted, setAttemptSubmitted] = useState(false);
  const [submittingAttempt, setSubmittingAttempt] = useState(false);
  const [readyForUserInput, setReadyForUserInput] = useState(true);
  const { playAudioSequence } = useAudioPlayer();
  const { playCorrectChime, playIncorrectBuzz } = useUISounds();
  const [prepareSessionLoading, setPrepareSessionLoading] = useState(false);
  const [prepareSessionSuccess, setPrepareSessionSuccess] = useState(false);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagError, setFlagError] = useState("");

  // Fetch the next dialog for the current session/profile
  const fetchNextDialog = useCallback(async () => {
    if (!selectedProfileId) return;
    setLoading(true);
    setError("");
    setUserAnsweredCorrectly(null);
    setStep(0);
    setAttemptSubmitted(false);

    try {
      // 1. Find open session for this profile
      const openSession = await getCurrentOpenSession(selectedProfileId);

      if (!openSession) {
        setDialog(null);
        setLoading(false);
        setSessionComplete(true);
        setPrepareSessionLoading(false);
        setPrepareSessionSuccess(false);
        return;
      }
      const sessionId = openSession.session_id;

      // 2. First try to find next session_word in STATE_READY
      let { data: sessionWords, error: swError } = await supabase
        .from("session_words")
        .select(
          `
          session_word_id,
          word_id,
          state,
          words!word_id(
            word_text,
            dialogs!word_id(
              dialog_id,
              sentence,
              teacher_question,
              student_answer,
              teacher_conclusion,
              is_correct,
              reviewed
            )
          )
        `
        )
        .eq("session_id", sessionId)
        .eq("state", STATE_READY)
        .limit(1);

      // If no READY words found, try REDO words
      if (!sessionWords || sessionWords.length === 0) {
        ({ data: sessionWords, error: swError } = await supabase
          .from("session_words")
          .select(
            `
            session_word_id,
            word_id,
            state,
            words!word_id(
              word_text,
              dialogs!word_id(
                dialog_id,
                sentence,
                teacher_question,
                student_answer,
                teacher_conclusion,
                is_correct,
                reviewed
              )
            )
          `
          )
          .eq("session_id", sessionId)
          .eq("state", STATE_REDO)
          .limit(1));
      }

      if (swError) throw swError;

      // If still no words available, end the session and prepare a new one
      if (!sessionWords || sessionWords.length === 0) {
        // Mark session as ended
        const { error: updateError } = await supabase
          .from("sessions")
          .update({ end_date: new Date().toISOString() })
          .eq("session_id", sessionId);

        if (updateError) throw updateError;

        // Set sessionComplete to true, but don't prepare a new session automatically
        setDialog(null);
        setSessionComplete(true);
        setLoading(false);
        setPrepareSessionLoading(false);
        setPrepareSessionSuccess(false);
        return;
      }

      const sw = sessionWords[0];
      // Randomly select a dialog instead of always taking the first one
      const dialogs = sw.words.dialogs;
      const dialog = dialogs[Math.floor(Math.random() * dialogs.length)];
      const gender = selectedProfile?.is_male ? "male" : "female";

      // Compose dialog object for UI with dynamically constructed URLs for media storage
      setDialog({
        ...dialog,
        session_word_id: sw.session_word_id,
        word: sw.words.word_text,
        image_url: `${MEDIA_BASE_URL}/gen-images/${dialog.dialog_id}.jpg`,
        teacher_question_url: `${MEDIA_BASE_URL}/gen-audio/${dialog.dialog_id}-teacher_question-${gender}.mp3`,
        student_answer_url: `${MEDIA_BASE_URL}/gen-audio/${dialog.dialog_id}-student_answer.mp3`,
        teacher_conclusion_url: `${MEDIA_BASE_URL}/gen-audio/${dialog.dialog_id}-teacher_conclusion-${gender}.mp3`,
      });
    } catch (err) {
      console.error("Error fetching dialog:", err);
      setError(err.message || "Failed to fetch dialog");
    } finally {
      setLoading(false);
    }
  }, [selectedProfileId, selectedProfile]);

  // Submit attempt
  const submitAttempt = async (dialogId, answeredCorrectly, dialogObj) => {
    // Insert attempt record
    const { error: attemptError } = await supabase.from("attempts").insert([
      {
        dialog_id: dialogId,
        session_word_id: dialogObj.session_word_id,
        is_correct: answeredCorrectly,
      },
    ]);
    if (attemptError) {
      console.error("Error inserting attempt:", attemptError);
      throw new Error(attemptError.message);
    }

    // Update session word state
    const { error: updateError } = await supabase
      .from("session_words")
      .update({ state: answeredCorrectly ? STATE_DONE : STATE_REDO })
      .eq("session_word_id", dialogObj.session_word_id);

    if (updateError) {
      console.error("Error updating session word state:", updateError);
      throw new Error(updateError.message);
    }
  };

  // Handle attempt submission
  const handleAttemptSubmission = async (answeredCorrectly) => {
    if (!dialog) return;
    try {
      setSubmittingAttempt(true);
      if (!attemptSubmitted) {
        await submitAttempt(dialog.dialog_id, answeredCorrectly, dialog);
        setAttemptSubmitted(true);
        setUserAnsweredCorrectly(answeredCorrectly);

        // Play appropriate sound feedback
        if (answeredCorrectly) {
          playCorrectChime();
          setStep(3);
        } else {
          playIncorrectBuzz();
        }
      } else {
        if (userAnsweredCorrectly === false && answeredCorrectly) {
          playCorrectChime();
          setReadyForUserInput(false);
          playAudioSequence([dialog.teacher_conclusion_url], () => {
            setReadyForUserInput(true);
          });
          setStep(3);
        }
      }
    } catch (err) {
      console.error("Error submitting attempt:", err);
      setError(err.message || "Failed to submit attempt");
    } finally {
      setSubmittingAttempt(false);
    }
  };

  // Handle next step
  const handleNext = () => {
    if (step === 3) {
      fetchNextDialog();
      return;
    }
    setReadyForUserInput(false);
    const nextStep = step + 1;
    setStep(nextStep);

    if (nextStep === 1) {
      playAudioSequence([dialog.teacher_question_url], () => {
        setReadyForUserInput(true);
      });
    } else if (nextStep === 2) {
      const audioSequence =
        Math.random() < 0.5
          ? [TEACHER_GENERIC_AUDIO_URL, dialog.student_answer_url]
          : [dialog.student_answer_url];
      playAudioSequence(audioSequence, () => {
        setReadyForUserInput(true);
      });
    } else {
      setReadyForUserInput(true);
    }
  };

  // Prepare a new session
  const handlePrepareSession = async () => {
    if (!selectedProfileId) return;
    setPrepareSessionLoading(true);
    setPrepareSessionSuccess(false);
    setError("");
    try {
      await prepareSessionForProfile(selectedProfileId);
      setPrepareSessionSuccess(true);
    } catch (err) {
      console.error("Error preparing session:", err);
      setError(
        err.message ||
          "Failed to prepare session. Check if the profile has wordlists assigned."
      );
    } finally {
      setPrepareSessionLoading(false);
    }
  };

  const handleFlagSubmit = async () => {
    if (!dialog || !selectedProfileId || !flagReason) return;
    setFlagSubmitting(true);
    setFlagError("");
    try {
      const { error: insertError } = await supabase
        .from("flagged_dialogs")
        .insert({
          profile_id: selectedProfileId,
          dialog_id: dialog.dialog_id,
          reason: flagReason,
        });
      if (insertError) throw insertError;
      setFlagModalOpen(false);
      setFlagReason("");
    } catch (err) {
      console.error("Error flagging dialog:", err);
      // Check for duplicate key constraint violation
      if (
        err.code === "23505" &&
        err.message.includes("flagged_dialogs_profile_dialog_uniq")
      ) {
        setFlagError("You have already reported this dialog. Thank you.");
      } else {
        setFlagError(err.message || "Failed to flag dialog");
      }
    } finally {
      setFlagSubmitting(false);
    }
  };

  const handleStartNewSession = useCallback(async () => {
    try {
      // Reset states first
      setSessionComplete(false);
      setError("");

      // Just fetch the next dialog from the already prepared session
      await fetchNextDialog();
    } catch (err) {
      console.error("Error starting new session:", err);
      setError(err.message || "Failed to start new session");
      setSessionComplete(true); // Show the completion screen again on error
    }
  }, [fetchNextDialog]);

  // Reset sessionComplete when component mounts or profile changes
  useEffect(() => {
    if (selectedProfileId) {
      setSessionComplete(false);
      setError("");
    }
  }, [selectedProfileId]);

  // Fetch dialog on mount and profile change
  useEffect(() => {
    if (selectedProfileId) {
      fetchNextDialog();
    }
  }, [fetchNextDialog, selectedProfileId]);

  if (!selectedProfileId) {
    return <ProfileSelection />;
  }

  return (
    <div className="h-full w-full">
      {error ? (
        <div className="text-center p-4 text-red-500">{error}</div>
      ) : loading ? (
        <div className="h-full w-full flex items-center justify-center">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
      ) : sessionComplete ? (
        <SessionComplete
          onPrepareSession={handlePrepareSession}
          prepareSessionLoading={prepareSessionLoading}
          prepareSessionSuccess={prepareSessionSuccess}
          onStartNewSession={handleStartNewSession}
        />
      ) : dialog ? (
        <div className="h-full w-full flex flex-col landscape:flex-row portrait-container relative">
          <div className="landscape-section-left portrait-image-container">
            <img
              src={dialog.image_url}
              alt={dialog.word}
              className="rounded-xl landscape-image portrait-image"
            />
          </div>
          <div className="landscape-section-right portrait-controls">
            <div className="landscape-controls-container">
              <div className="text-center mb-4">
                <h2 className="text-2xl font-bold text-primary">
                  {dialog.word}
                </h2>
              </div>
              <div className="flex justify-center">
                <DialogControls
                  step={step}
                  isCorrect={dialog.is_correct}
                  userAnsweredCorrectly={userAnsweredCorrectly}
                  onAttempt={handleAttemptSubmission}
                  onNext={handleNext}
                  hidden={!readyForUserInput}
                  profile={selectedProfile}
                  submittingAttempt={submittingAttempt}
                />
              </div>
            </div>
          </div>
          <button
            className="btn btn-xs btn-outline absolute bottom-4 right-4 z-10"
            onClick={() => setFlagModalOpen(true)}
          >
            🚩 Report Issue
          </button>
        </div>
      ) : null}

      {flagModalOpen && (
        <div className="modal modal-open">
          <div className="modal-box relative w-full max-w-sm">
            <button
              className="btn btn-sm btn-circle absolute right-2 top-2"
              onClick={() => setFlagModalOpen(false)}
              disabled={flagSubmitting}
            >
              ✕
            </button>
            <h3 className="text-lg font-bold mb-4">Flag this Dialog</h3>
            {flagError && (
              <div className="alert alert-error mb-2">
                <span>{flagError}</span>
              </div>
            )}
            <select
              className="select select-bordered w-full mb-4"
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
            >
              <option value="">Select a reason</option>
              <option value="Image is weird">Image is weird</option>
              <option value="Sentence doesn't make sense">
                Sentence doesn't make sense
              </option>
              <option value="Teacher said it wrong">
                Teacher said it wrong
              </option>
              <option value="Other">other</option>
            </select>
            <div className="modal-action">
              <button
                className="btn btn-ghost mr-2"
                onClick={() => setFlagModalOpen(false)}
                disabled={flagSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleFlagSubmit}
                disabled={flagSubmitting || !flagReason}
              >
                {flagSubmitting ? (
                  <span className="loading loading-spinner loading-xs"></span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop bg-black/50"
            onClick={() => setFlagModalOpen(false)}
          ></div>
        </div>
      )}
    </div>
  );
}

export default DialogApp;
